import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const fmtErr = (d) => (typeof d === "string" ? d : "Login failed. Please try again.");

export default function Login() {
  const { login } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(email, password); navigate("/"); }
    catch (ex) { setErr(fmtErr(ex.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-slate-900 px-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl shadow-sm" style={{ backgroundColor: "hsl(var(--brand-accent))" }}>
            <svg viewBox="0 0 24 24" className="h-6 w-6 text-white" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2 4 5v6c0 5 3.5 8.5 8 11 4.5-2.5 8-6 8-11V5l-8-3Z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <div className="leading-tight">
            <div className="font-display text-base font-extrabold tracking-tight text-white">{settings?.firm_name || "PRO ACCOUNTANTS"}</div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{settings?.tagline || "London"}</div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-white p-7 shadow-xl">
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Sign in</h1>
          <p className="mt-1 text-sm text-slate-500">Team access to the enquiry & onboarding tracker.</p>

          <form onSubmit={submit} className="mt-6 grid gap-4">
            <div className="grid gap-1.5">
              <Label>Email</Label>
              <Input type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@firm.co.uk" autoComplete="username" />
            </div>
            <div className="grid gap-1.5">
              <Label>Password</Label>
              <Input type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
            </div>
            {err && <p className="text-sm font-medium text-rose-600" data-testid="login-error">{err}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" style={{ backgroundColor: "hsl(var(--brand))" }} className="mt-1 h-11 rounded-full text-white">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogIn className="mr-2 h-4 w-4" />}
              Sign in
            </Button>
          </form>
        </div>
        <p className="mt-5 text-center text-xs text-slate-500">Authorised staff only. Looking to enquire? <a href="/enquire" className="font-semibold text-slate-300 hover:text-white">Use our enquiry form</a>.</p>
      </div>
    </div>
  );
}
